/**
 * The workspace's pay program — the single source of truth for what a team
 * earns, and the only place that turns rules into money.
 *
 * WHY THIS EXISTS
 * The earnings card used to hardcode its own breakdown: a commission line plus
 * two named signup lines ("Clean 365", "VIP") reading server fields that were
 * never wired. That made it a sales-only card — a Technicians workspace could
 * not use it without a developer. Here the same card is a calculator, and an
 * admin composes the rules from that workspace's OWN metrics, so any team
 * (technicians, door-to-door, a company we sell this to tomorrow) configures
 * itself.
 *
 * THE TWO RULE TYPES
 *   A cut         X% of a money metric        "1% of Estimate Won Value"
 *   A flat bonus  $X for every counted thing  "$10 for every Membership"
 *
 * A flat bonus may additionally target ONE VALUE INSIDE the metric's entries.
 * Memberships is a single metric whose entries each record a `plan_name`
 * (Clean 365 / Clean Start / Clean 365 VIP) — so "Clean 365 signups" and "VIP
 * signups" are two rules on one metric, not two metrics. That is `where`.
 *
 * MONEY IS NEVER RECOMPUTED BACKWARDS
 * Changing a live rate does not rewrite what was already earned. A rule keeps
 * its history — [{ pct/amount, until }] — and each period is valued at the rate
 * that was in force. Raising commission to 2% in August cannot re-price July.
 *
 * PERIODS
 * Earnings is a paycheck view (this month). The ladder is an annual program
 * (year to date). Both read the same program; only the window differs.
 *
 * Everything here is pure — no fetching, no React — so cards, the editor's live
 * preview and tests all agree by construction.
 */

/* ── shape ──────────────────────────────────────────────────────────────── */

export const emptyProgram = () => ({
  scoredOnKpiId: null, // the money metric the ladder climbs
  commissions: [], // [{ kpiId, pct, label?, effFrom?, history?: [{ pct, until }] }]
  bonuses: [], // [{ kpiId, where, amount, label, effFrom?, history? }]
  flats: [], // [{ label, amount, per, note, informational }] — NOT from a metric
  milestones: [], // [{ target, bonus }] ascending
});

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const str = (v) => (typeof v === "string" ? v.trim() : "");
/* Combined-pay labels round-trip through sanitizeCombinedPay on every
   keystroke (CombinedEarningsSetup writes on every change, not just on
   blur/save). str()'s .trim() stripped a just-typed trailing space before
   the next character landed, so a space pressed at the end of the text you
   were typing — the common case — silently vanished. Cap the length and
   still null out a whitespace-only label, but keep the raw (untrimmed)
   text otherwise so a live edit's spaces survive the round trip. */
const rawLabel = (v, max) => {
  const s = typeof v === "string" ? v.slice(0, max) : "";
  return s.trim() ? s : null;
};
/* Local money formatter — kept here so this file stays free of UI imports. */
const money = (n) => `$${Math.round(num(n)).toLocaleString()}`;

/** A rule only pays once it names a metric and carries a positive value. */
export const isLiveCommission = (c) => !!(c && c.kpiId && num(c.pct) > 0);
export const isLiveBonus = (b) => !!(b && b.kpiId && num(b.amount) > 0);
/* A flat line is not from a metric — it just needs a name. It can be a real
   payment (adds to the total) or informational (shown as context, not added). */
export const FLAT_PERIODS = ["month", "year", "once"];
export const isLiveFlat = (f) => !!(f && str(f.label));

export const hasEarningsRules = (p) =>
  !!p &&
  ((p.commissions || []).some(isLiveCommission) ||
    (p.bonuses || []).some(isLiveBonus) ||
    (p.flats || []).some(isLiveFlat));

/** Blank scaffold rows are ignored everywhere — a ladder needs a real target. */
export const realRungs = (p) =>
  ((p && p.milestones) || [])
    .filter((m) => num(m.target) > 0 && num(m.bonus) >= 0)
    .sort((a, b) => a.target - b.target);

export const hasLadder = (p) => !!(p && p.scoredOnKpiId && realRungs(p).length > 0);

/* ── persistence shape ──────────────────────────────────────────────────── */

const sanitizeHistory = (h) =>
  (Array.isArray(h) ? h : [])
    .map((e) => ({
      ...(e.pct != null ? { pct: num(e.pct) } : {}),
      ...(e.amount != null ? { amount: num(e.amount) } : {}),
      until: str(e.until) || null,
    }))
    .filter((e) => e.until);

/* ── when a rule applies ────────────────────────────────────────────────────
   A bonus can pay across the whole year, or only inside one slice of it — the
   comp-sheet pattern of "Q1–Q3 performance bonus" and a separate "Q4 bonus".
   The preset is stored (so a quarter stays this year's quarter next year); a
   custom range stores its typed dates. resolveApplies turns either into the
   from/to the (server-side) bonus engine scores against. */
export const APPLIES_PRESETS = [
  { key: "year", label: "Whole year" },
  { key: "q1", label: "Q1" },
  { key: "q2", label: "Q2" },
  { key: "q3", label: "Q3" },
  { key: "q4", label: "Q4" },
  { key: "q13", label: "Q1–Q3" },
  { key: "h2", label: "H2" },
  { key: "custom", label: "Custom range" },
];
// month is 1-based: [fromMonth, fromDay, toMonth, toDay]
const PRESET_SPAN = {
  year: [1, 1, 12, 31],
  q1: [1, 1, 3, 31],
  q2: [4, 1, 6, 30],
  q3: [7, 1, 9, 30],
  q4: [10, 1, 12, 31],
  q13: [1, 1, 9, 30],
  h2: [7, 1, 12, 31],
};
const pad2 = (x) => String(x).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

/** The preset a rule is on ("year" when unset). */
export const appliesPreset = (a) => {
  const key = a && a.preset;
  return APPLIES_PRESETS.some((p) => p.key === key) ? key : "year";
};

/** Human label for a rule's window ("Whole year", "Q1–Q3", "Custom range"). */
export const appliesLabel = (a) => {
  const key = appliesPreset(a);
  const p = APPLIES_PRESETS.find((x) => x.key === key);
  return p ? p.label : "Whole year";
};

/** The from/to a rule's window means, for a given year (default: this year). */
export const resolveApplies = (a, year) => {
  const key = appliesPreset(a);
  if (key === "custom") return { from: str(a && a.from) || null, to: str(a && a.to) || null };
  const y = year || new Date().getFullYear();
  const s = PRESET_SPAN[key] || PRESET_SPAN.year;
  return { from: ymd(y, s[0], s[1]), to: ymd(y, s[2], s[3]) };
};

/** Persist shape: the preset, plus typed dates only for a custom range. */
export const sanitizeApplies = (a) => {
  const key = appliesPreset(a);
  if (key === "custom") return { preset: "custom", from: str(a && a.from) || null, to: str(a && a.to) || null };
  return { preset: key };
};

/**
 * Normalize a program to exactly what we persist. Anything not named here is
 * dropped — the same discipline as the layout store, for the same reason:
 * silent extra keys become silent bugs.
 */
export const sanitizeProgram = (p) => {
  const src = p || {};
  return {
    scoredOnKpiId: src.scoredOnKpiId || null,
    commissions: (Array.isArray(src.commissions) ? src.commissions : [])
      .map((c) => ({
        kpiId: c.kpiId || null,
        pct: num(c.pct),
        // A cut can be renamed for the card — "Job commission" instead of the
        // generic "Commission" — the same way a bonus can.
        label: str(c.label).slice(0, 60) || null,
        effFrom: str(c.effFrom) || null,
        history: sanitizeHistory(c.history),
      }))
      .filter((c) => c.kpiId && c.pct > 0),
    bonuses: (Array.isArray(src.bonuses) ? src.bonuses : [])
      .map((b) => ({
        kpiId: b.kpiId || null,
        where: str(b.where) || null,
        amount: num(b.amount),
        label: str(b.label).slice(0, 60) || null,
        appliesTo: sanitizeApplies(b.appliesTo),
        effFrom: str(b.effFrom) || null,
        history: sanitizeHistory(b.history),
      }))
      .filter((b) => b.kpiId && b.amount > 0),
    flats: (Array.isArray(src.flats) ? src.flats : [])
      .map((f) => ({
        label: str(f.label).slice(0, 60) || null,
        amount: num(f.amount),
        per: FLAT_PERIODS.indexOf(f.per) >= 0 ? f.per : "month",
        note: str(f.note).slice(0, 80) || null,
        appliesTo: sanitizeApplies(f.appliesTo),
        informational: !!f.informational,
      }))
      .filter((f) => f.label),
    milestones: realRungs(src).map((m) => ({ target: num(m.target), bonus: num(m.bonus) })),
  };
};

/**
 * Read a stored bonusConfig into a program.
 *
 * MIGRATION: every workspace configured before money rules holds the old shape
 * — { revenueKpiId, commissionPct, milestones }. That becomes the first
 * commission rule plus the scored-on metric, so an existing ladder keeps
 * working untouched and nobody has to reconfigure.
 */
export const programFromConfig = (cfg) => {
  const c = cfg || {};
  if (c.program) return sanitizeProgram(c.program);
  const legacyPct = num(c.commissionPct);
  return sanitizeProgram({
    scoredOnKpiId: c.revenueKpiId || null,
    commissions: legacyPct > 0 && c.revenueKpiId ? [{ kpiId: c.revenueKpiId, pct: legacyPct }] : [],
    bonuses: [],
    milestones: c.milestones,
  });
};

/**
 * Write a program back into a bonusConfig, keeping the legacy keys in step so
 * an older client (or the server's current controller) still reads a sane
 * commission and ladder.
 */
export const configFromProgram = (program) => {
  const p = sanitizeProgram(program);
  const firstCut = p.commissions[0] || null;
  return {
    revenueKpiId: p.scoredOnKpiId || (firstCut && firstCut.kpiId) || null,
    commissionPct: firstCut ? firstCut.pct : 0,
    milestones: p.milestones,
    program: p,
  };
};

/* ── dated changes ──────────────────────────────────────────────────────── */

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Apply a rate change that starts on a date. The old rate is pushed onto the
 * rule's history with the day it stopped applying; nothing already earned
 * moves. This is what makes "we now pay 2%" safe to click.
 */
export const withDatedChange = (rule, nextValue, effFromISO, key = "pct") => {
  const current = num(rule[key]);
  const next = num(nextValue);
  if (!effFromISO || next === current) return rule;
  // If the current rate has not applied for a single day yet (its own start
  // is on or after the new start), it never priced anything — correct it in
  // place instead of recording an empty slice. Without this, re-editing a
  // just-changed rule stacked ghost history entries ($15,555 forever).
  if (rule.effFrom && effFromISO <= rule.effFrom) {
    return { ...rule, [key]: next, effFrom: effFromISO };
  }
  const eff = new Date(`${effFromISO}T12:00:00`);
  const until = new Date(eff.getTime() - 86400000).toISOString().slice(0, 10);
  return {
    ...rule,
    [key]: next,
    effFrom: effFromISO,
    history: [...(rule.history || []), { [key]: current, until }],
  };
};

/**
 * How a rule reads on the card. A rule states itself plainly unless a rate
 * change still touches the month on screen — only then does the split show,
 * so nobody has to guess which rate applied when. Once the old rate's last
 * day predates the current month it is dead history: the whole visible period
 * is on the new rate, and printing "$15,555 until Jul 31" forever only reads
 * like a bug.
 */
export const ruleSummary = (rule, key = "pct", now = new Date()) => {
  const hist = (rule && rule.history) || [];
  if (!hist.length) return null;
  const last = hist[hist.length - 1];
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  if (!last.until || last.until < monthStart) return null;
  const unit = key === "pct" ? "%" : "";
  const pre = key === "pct" ? "" : "$";
  return `${pre}${num(last[key])}${unit} until ${fmtDay(last.until)} · ${pre}${num(rule[key])}${unit} from ${fmtDay(rule.effFrom)}`;
};

/* ── computing the money ────────────────────────────────────────────────── */

/**
 * Build the earnings breakdown for one player.
 *
 * `amounts` is what the server (or the demo snapshot) already aggregated for
 * this player, keyed by kpiId:
 *     { [kpiId]: { month, year, plans?: { [planValue]: { month, year } } } }
 * `plans` only exists for metrics whose entries carry a grouping detail.
 *
 * Returns rows in the card's own order — commission(s), bonuses, then the
 * milestone money banked inside this window — plus the total.
 */
export const earningsBreakdown = ({ program, amounts = {}, kpiLabel = () => "", period = "month", bankedThisPeriod = 0 }) => {
  const p = sanitizeProgram(program);
  const per = period === "year" ? "year" : "month";
  // Plan names are free text ("Clean 365", "clean 365 ") — the rule's saved
  // `where` and the payload's plan key can carry different spellings, so an
  // exact-key miss falls back to a normalized match instead of silently
  // paying $0.
  const normPlan = (v) => String(v).trim().replace(/\s+/g, " ").toLowerCase();
  const amountFor = (kpiId, where) => {
    const a = amounts[kpiId] || {};
    if (where) {
      const plans = a.plans || {};
      const plan =
        plans[where] ||
        plans[Object.keys(plans).find((k) => normPlan(k) === normPlan(where))];
      return plan ? num(plan[per]) : 0;
    }
    return num(a[per]);
  };

  const rows = [];

  // The key is the ROW INDEX, not the rule's content. Two rules can legitimately
  // share a metric and condition ("$10 for every Clean 365" twice), and a
  // content-based key made them collide — React deduped the second row, so an
  // added bonus raised the total but never appeared as a line. The index is the
  // only thing guaranteed unique per row.
  p.commissions.forEach((c, i) => {
    const base = amountFor(c.kpiId, null);
    const earn = (base * c.pct) / 100;
    rows.push({
      kind: "commission",
      key: `c-${i}`,
      label: c.label || "Commission",
      detail: ruleSummary(c, "pct") || `${c.pct}% of ${money(base)}`,
      value: earn,
      missing: !kpiLabel(c.kpiId),
    });
  });

  p.bonuses.forEach((b, i) => {
    const count = amountFor(b.kpiId, b.where);
    const name = b.label || (b.where ? `${b.where} — ${kpiLabel(b.kpiId)}` : kpiLabel(b.kpiId));
    /* Qty can be a $ average (team rollup) — never print float noise like
       82050.58666666667 in the "how it adds up" sub-line. */
    const qty = Math.round((Number(count) || 0) * 100) / 100;
    const qtyLabel = qty.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    const rate = Math.round((Number(b.amount) || 0) * 100) / 100;
    const rateLabel = rate.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    rows.push({
      kind: "bonus",
      key: `b-${i}`,
      label: name || "Bonus",
      detail: `${qtyLabel} × $${rateLabel}`,
      value: qty * rate,
      dim: !qty,
      missing: !kpiLabel(b.kpiId),
    });
  });

  // Flat lines aren't from a metric. An informational one shows as context and
  // adds nothing; a real one adds its amount, converted to the window we're
  // showing (a "$6,000/year" line is $500 in a month view).
  const cadenceOf = { year: "per year", once: "one time", month: "per month" };
  (p.flats || []).forEach((f, i) => {
    const cadence = cadenceOf[f.per] || "per month";
    let value = 0;
    if (!f.informational) {
      if (per === "month") value = f.per === "year" ? f.amount / 12 : f.amount;
      else value = f.per === "month" ? f.amount * 12 : f.amount;
    }
    rows.push({
      kind: "flat",
      key: `f-${i}`,
      label: f.label || "Flat bonus",
      detail: f.note || (f.informational ? "informational" : cadence),
      value,
      dim: f.informational,
      informational: f.informational,
    });
  });

  // Milestone money only shows in the window it was actually banked in — a rung
  // crossed in April is not "this month's" pay.
  if (bankedThisPeriod > 0) {
    rows.push({ kind: "milestone", key: "milestone", label: "Milestone", value: bankedThisPeriod });
  }

  return { rows, total: rows.reduce((s, r) => s + r.value, 0) };
};

/**
 * Multi-workspace "How it adds up" shaping.
 *
 * mergeMultiWorkspaceBonus prefixes every rule with the workspace name. Modes:
 *   merge  — one line per pay kind; money summed; card names joined with " + "
 *            (or a single override title). Default for multi-workspace cards.
 *   strip  — keep every rule as its own line, but drop "Field Sales — " so
 *            "What to call it on the card" stays the title.
 */
const KIND_ORDER = ["commission", "bonus", "flat", "milestone"];
const KIND_LABEL = {
  commission: "Commission",
  bonus: "Bonuses",
  flat: "Flat bonuses",
  milestone: "Milestone",
};

/** "Field Sales — Q1 > Q3 Sold Revenue" → "Q1 > Q3 Sold Revenue" */
const stripWorkspacePrefix = (label) => {
  const s = String(label || "").trim();
  const cut = s.indexOf(" — ");
  if (cut < 0) return s;
  const rest = s.slice(cut + 3).trim();
  return rest || s;
};

export const combineEarningsRows = (rows, {
  across = null,
  merge = true,
  title = null,
} = {}) => {
  if (!Array.isArray(rows) || !rows.length) return rows || [];

  /* Separate lines — still drop the workspace prefix so the custom card name
     is what you read. */
  if (!merge) {
    return rows.map((r, i) => ({
      ...r,
      key: r.key || `row-${i}`,
      label: stripWorkspacePrefix(r.label) || r.label,
    }));
  }

  if (rows.length < 2) {
    const [only] = rows;
    return [{
      ...only,
      label: (title && String(title).trim())
        || stripWorkspacePrefix(only.label)
        || only.label,
    }];
  }

  const buckets = {};
  rows.forEach((r) => {
    const kind = r.kind || "other";
    if (!buckets[kind]) {
      buckets[kind] = {
        kind,
        key: `combo-${kind}`,
        names: [],
        value: 0,
        details: [],
        dim: true,
        missing: false,
        informational: true,
      };
    }
    const b = buckets[kind];
    const name = stripWorkspacePrefix(r.label);
    if (name && !b.names.includes(name)) b.names.push(name);
    b.value += Number(r.value) || 0;
    if (r.detail) b.details.push(r.detail);
    if (!r.dim) b.dim = false;
    if (r.missing) b.missing = true;
    if (!r.informational) b.informational = false;
  });

  const override = typeof title === "string" && title.trim() ? title.trim() : null;
  const keys = [
    ...KIND_ORDER.filter((k) => buckets[k]),
    ...Object.keys(buckets).filter((k) => !KIND_ORDER.includes(k)),
  ];
  return keys.map((k) => {
    const b = buckets[k];
    const { names, details } = b;
    let detail = across || null;
    if (!detail && details.length === 1) {
      const [only] = details;
      detail = only;
    } else if (!detail && details.length > 1) {
      detail = `${details.length} rules combined`;
    }
    return {
      kind: b.kind,
      key: b.key,
      label: override || (names.length ? names.join(" + ") : (KIND_LABEL[k] || "Other")),
      value: b.value,
      detail,
      dim: b.dim,
      missing: b.missing,
      informational: b.informational,
    };
  });
};

/* ── multi-workspace combined pay (one rule, metrics per workspace) ─────── */

const mkKey = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

export const emptyCombinedPay = () => ({
  commissions: [],
  bonuses: [],
  flats: [],
});

/** Persist shape for Analytics multi-ws Anticipated earnings Setup. */
export const sanitizeCombinedPay = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  const cleanSources = (sources) => {
    if (!sources || typeof sources !== "object") return {};
    const out = {};
    Object.entries(sources).forEach(([wsId, kpiId]) => {
      if (typeof wsId !== "string" || !wsId) return;
      const id = kpiId == null ? "" : String(kpiId).trim();
      if (!id) return;
      out[wsId.slice(0, 40)] = id.slice(0, 40);
    });
    return out;
  };
  const commissions = (Array.isArray(src.commissions) ? src.commissions : [])
    .filter((c) => c && typeof c === "object")
    .slice(0, 24)
    .map((c, i) => ({
      key: str(c.key) || `c-${i}`,
      label: rawLabel(c.label, 80),
      pct: num(c.pct),
      mode: c.mode === "sum" ? "sum" : "avg",
      sources: cleanSources(c.sources),
      appliesTo: sanitizeApplies(c.appliesTo),
    }));
  const bonuses = (Array.isArray(src.bonuses) ? src.bonuses : [])
    .filter((b) => b && typeof b === "object")
    .slice(0, 24)
    .map((b, i) => ({
      key: str(b.key) || `b-${i}`,
      label: rawLabel(b.label, 80),
      amount: num(b.amount),
      mode: b.mode === "avg" ? "avg" : "sum",
      sources: cleanSources(b.sources),
      appliesTo: sanitizeApplies(b.appliesTo),
    }));
  const flats = (Array.isArray(src.flats) ? src.flats : [])
    .filter((f) => f && typeof f === "object" && str(f.label))
    .slice(0, 24)
    .map((f, i) => ({
      key: str(f.key) || `f-${i}`,
      label: rawLabel(f.label, 80) || "",
      amount: num(f.amount),
      per: FLAT_PERIODS.includes(f.per) ? f.per : "month",
      note: str(f.note).slice(0, 80) || null,
      informational: !!f.informational,
      appliesTo: sanitizeApplies(f.appliesTo),
    }));
  return { commissions, bonuses, flats };
};

/* A rule counts as "set up" once it names which metric feeds it — the amount
   itself can legitimately be 0 (an admin zeroing a bonus for now without
   deleting its workspace mapping), so requiring a positive amount here used
   to drop the card back to the "nothing set up yet" empty state the moment
   someone typed 0, even though the rule was still very much configured. */
export const hasCombinedPayRules = (pay) => {
  if (!pay) return false;
  return (
    (pay.bonuses || []).some((b) => Object.keys(b.sources || {}).length > 0)
    || (pay.commissions || []).some((c) => Object.keys(c.sources || {}).length > 0)
    || (pay.flats || []).some((f) => str(f.label))
  );
};

/** Seed one combined bonus from the old per-workspace bonusPrograms map. */
export const seedCombinedPayFromPrograms = (bonusPrograms, wsIds) => {
  const ids = (wsIds || []).map(String).filter(Boolean);
  const sources = {};
  const names = [];
  let amount = 1;
  let appliesTo = { preset: "custom", from: null, to: null };
  ids.forEach((wsId) => {
    const prog = sanitizeProgram((bonusPrograms && bonusPrograms[wsId]) || {});
    const b = (prog.bonuses || []).find(isLiveBonus);
    if (b && b.kpiId) {
      sources[wsId] = String(b.kpiId);
      amount = num(b.amount) || 1;
      if (str(b.label)) names.push(str(b.label));
      if (b.appliesTo) appliesTo = sanitizeApplies(b.appliesTo);
    }
  });
  const pay = emptyCombinedPay();
  if (Object.keys(sources).length) {
    pay.bonuses.push({
      key: mkKey("b"),
      label: names.join(" + ") || null,
      amount,
      mode: "sum",
      sources,
      appliesTo,
    });
  }
  /* Flats: take from the first workspace that has them (shared, not per-ws). */
  for (let i = 0; i < ids.length; i += 1) {
    const prog = sanitizeProgram((bonusPrograms && bonusPrograms[ids[i]]) || {});
    const flats = (prog.flats || []).filter(isLiveFlat);
    if (flats.length) {
      pay.flats = flats.map((f, j) => ({
        key: mkKey("f") + j,
        label: f.label,
        amount: f.amount,
        per: f.per || "month",
        note: f.note || null,
        informational: !!f.informational,
        appliesTo: sanitizeApplies(f.appliesTo),
      }));
      break;
    }
  }
  return pay;
};

/**
 * Mirror combined rules into per-workspace programs so bonus-progress still
 * fetches amounts for every sourced KPI.
 */
export const combinedPayToPrograms = (combinedPay, wsIds) => {
  const pay = sanitizeCombinedPay(combinedPay);
  const map = {};
  (wsIds || []).map(String).filter(Boolean).forEach((wsId) => {
    const commissions = (pay.commissions || [])
      .filter((c) => c.sources && c.sources[wsId] && num(c.pct) > 0)
      .map((c) => ({
        kpiId: c.sources[wsId],
        pct: c.pct,
        label: c.label,
      }));
    const bonuses = (pay.bonuses || [])
      .filter((b) => b.sources && b.sources[wsId] && num(b.amount) > 0)
      .map((b) => ({
        kpiId: b.sources[wsId],
        amount: b.amount,
        label: b.label,
        where: null,
        appliesTo: b.appliesTo,
      }));
    map[wsId] = sanitizeProgram({
      commissions,
      bonuses,
      flats: (pay.flats || []).map((f) => ({
        ...f,
        appliesTo: f.appliesTo,
      })),
      milestones: [],
      scoredOnKpiId: null,
    });
  });
  return map;
};

const poolSources = (sources, amounts, mode) => {
  const vals = Object.values(sources || {})
    .map((kpiId) => {
      const a = amounts && amounts[kpiId];
      return a && a.month != null ? num(a.month) : 0;
    });
  if (!vals.length) return 0;
  const sum = vals.reduce((s, v) => s + v, 0);
  return mode === "avg" ? sum / vals.length : sum;
};

const fmtQty = (n) => {
  const q = Math.round(num(n) * 100) / 100;
  return q.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
};

/** Stable key for a rule's Applies-to window, or null → use the card window. */
export const appliesRangeKey = (a) => {
  if (!a) return null;
  const resolved = resolveApplies(a);
  const from = str(resolved && resolved.from);
  const to = str(resolved && resolved.to);
  if (!from && !to) return null;
  return `${from}~${to}`;
};

/** Unique Applies-to { key, from, to } windows on a combined pay plan. */
export const listCombinedAppliesRanges = (combinedPay) => {
  const pay = sanitizeCombinedPay(combinedPay);
  const seen = new Set();
  const out = [];
  [...(pay.commissions || []), ...(pay.bonuses || []), ...(pay.flats || [])].forEach((rule) => {
    const key = appliesRangeKey(rule.appliesTo);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const resolved = resolveApplies(rule.appliesTo);
    out.push({ key, from: resolved.from || null, to: resolved.to || null });
  });
  return out;
};

/**
 * Breakdown rows from a combined multi-ws pay plan (not per-workspace rules).
 * `amounts` = card-window totals. `appliesAmounts` = { [appliesRangeKey]: amounts }
 * so each rule with Applies-to uses its own dated slice.
 */
export const combinedPayBreakdown = (combinedPay, amounts = {}, appliesAmounts = {}) => {
  const pay = sanitizeCombinedPay(combinedPay);
  const rows = [];
  const bagFor = (appliesTo) => {
    const key = appliesRangeKey(appliesTo);
    if (key && appliesAmounts && appliesAmounts[key]) return appliesAmounts[key];
    return amounts;
  };

  (pay.commissions || []).forEach((c, i) => {
    if (!Object.keys(c.sources || {}).length) return;
    const bag = bagFor(c.appliesTo);
    const base = poolSources(c.sources, bag, c.mode);
    const value = (base * c.pct) / 100;
    rows.push({
      kind: "commission",
      key: c.key || `cc-${i}`,
      label: c.label || "Commission",
      detail: `${c.pct}% of ${money(base)}${c.mode === "avg" ? " (avg)" : ""}`,
      value,
      dim: !base,
    });
  });

  (pay.bonuses || []).forEach((b, i) => {
    if (!Object.keys(b.sources || {}).length) return;
    const bag = bagFor(b.appliesTo);
    const qty = poolSources(b.sources, bag, b.mode);
    const rate = Math.round(num(b.amount) * 100) / 100;
    rows.push({
      kind: "bonus",
      key: b.key || `cb-${i}`,
      label: b.label || "Bonus",
      detail: `${fmtQty(qty)} × $${fmtQty(rate)}${b.mode === "avg" ? " (avg)" : ""}`,
      value: qty * rate,
      dim: !qty,
    });
  });

  const cadenceOf = { year: "per year", once: "one time", month: "per month" };
  (pay.flats || []).forEach((f, i) => {
    let value = 0;
    if (!f.informational) {
      value = f.per === "year" ? f.amount / 12 : f.amount;
    }
    rows.push({
      kind: "flat",
      key: f.key || `cf-${i}`,
      label: f.label || "Flat bonus",
      detail: f.note || (f.informational ? "informational" : cadenceOf[f.per] || "per month"),
      value,
      dim: !!f.informational,
      informational: !!f.informational,
    });
  });

  return { rows, total: rows.reduce((s, r) => s + (Number(r.value) || 0), 0) };
};

/** Ladder state for a YTD figure: what's banked, and what's next. */
export const ladderState = (program, ytd) => {
  const rungs = realRungs(program);
  const banked = rungs.filter((m) => ytd >= m.target);
  return {
    rungs,
    banked: banked.reduce((s, m) => s + m.bonus, 0),
    bankedRungs: banked,
    next: rungs.find((m) => ytd < m.target) || null,
  };
};

/** Paid money can't be edited away — a banked rung is locked in the editor. */
export const isRungBanked = (program, ytd, rung) =>
  hasLadder(program) && num(rung.target) > 0 && num(ytd) >= num(rung.target);

